import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  increment,
  arrayUnion
  // runTransaction - not currently used, but kept for future use
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  timestampToDateString,
  timestampToDateTimeString,
  sortByTimestamp,
  handleIndexError
} from '../utils/firestoreHelpers';

// Users Collection
export const usersService = {
  async createUser(userId, userData) {
    // Use userId as document ID (from Firebase Auth)
    const userRef = doc(db, 'users', userId);
    
    // Check if document already exists
    const docSnap = await getDoc(userRef);
    
    if (docSnap.exists()) {
      // Document exists, update it
      await updateDoc(userRef, {
        ...userData,
        updatedAt: Timestamp.now()
      });
    } else {
      // Document doesn't exist, create it
      await setDoc(userRef, {
        ...userData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }
    return userId;
  },

  async getUser(userId) {
    const docSnap = await getDoc(doc(db, 'users', userId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  },

  async updateUser(userId, userData) {
    await updateDoc(doc(db, 'users', userId), {
      ...userData,
      updatedAt: Timestamp.now()
    });
  },

  async deleteUser(userId) {
    await deleteDoc(doc(db, 'users', userId));
  },

  /**
   * Get all students
   * @returns {Promise<Array>} Array of student user objects
   */
  async getAllStudents() {
    try {
      const q = query(
        collection(db, 'users'),
        where('type', '==', 'student'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return handleIndexError(
        error,
        'students',
        async () => {
          const simpleQuery = query(collection(db, 'users'), where('type', '==', 'student'));
          return await getDocs(simpleQuery);
        },
        (students) => sortByTimestamp(students, 'createdAt', 'desc')
      );
    }
  },

  /**
   * Get all coaches
   * @returns {Promise<Array>} Array of coach user objects
   */
  async getAllCoaches() {
    try {
      const q = query(
        collection(db, 'users'),
        where('type', '==', 'coach'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      return handleIndexError(
        error,
        'coaches',
        async () => {
          const simpleQuery = query(collection(db, 'users'), where('type', '==', 'coach'));
          return await getDocs(simpleQuery);
        },
        (coaches) => sortByTimestamp(coaches, 'createdAt', 'desc')
      );
    }
  },

  subscribeToAllUsers(callback, errorCallback) {
    // Subscribe to all users (students and coaches)
    const usersRef = collection(db, 'users');
    return onSnapshot(usersRef,
      (snapshot) => {
        const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Filter to students and coaches, sort by createdAt
        const filtered = users.filter(u => u.type === 'student' || u.type === 'coach');
        filtered.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
          return bTime - aTime;
        });
        callback(filtered);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          console.error('Error in users subscription:', error);
        }
      }
    );
  },

  subscribeToStudents(callback, errorCallback) {
    // Try with orderBy first, fallback to simple query if index missing
    const q = query(
      collection(db, 'users'),
      where('type', '==', 'student'),
      orderBy('createdAt', 'desc')
    );
    
    let unsubscribeFn = null;
    
    try {
      unsubscribeFn = onSnapshot(q, 
        (snapshot) => {
          const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          callback(students);
        },
        (error) => {
          // If index missing, try without orderBy
          if (error.code === 'failed-precondition') {
            console.warn('Index missing for students. Using simple query without sorting.');
            const simpleQuery = query(collection(db, 'users'), where('type', '==', 'student'));
            unsubscribeFn = onSnapshot(simpleQuery, 
              (snapshot) => {
                const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                // Sort manually by createdAt
                students.sort((a, b) => {
                  const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
                  const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
                  return bTime - aTime;
                });
                callback(students);
              },
              (fallbackError) => {
                if (errorCallback) {
                  errorCallback(fallbackError);
                } else {
                  console.error('Error in students subscription (fallback):', fallbackError);
                }
              }
            );
          } else {
            if (errorCallback) {
              errorCallback(error);
            } else {
              console.error('Error in students subscription:', error);
            }
          }
        }
      );
    } catch (error) {
      // Handle synchronous errors
      if (error.code === 'failed-precondition') {
        console.warn('Index missing for students. Using simple query without sorting.');
        const simpleQuery = query(collection(db, 'users'), where('type', '==', 'student'));
        unsubscribeFn = onSnapshot(simpleQuery, 
          (snapshot) => {
            const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort manually by createdAt
            students.sort((a, b) => {
              const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
              const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
              return bTime - aTime;
            });
            callback(students);
          },
          (fallbackError) => {
            if (errorCallback) {
              errorCallback(fallbackError);
            } else {
              console.error('Error in students subscription (fallback):', fallbackError);
            }
          }
        );
      } else {
        throw error;
      }
    }
    
    return () => {
      if (unsubscribeFn) {
        unsubscribeFn();
      }
    };
  }
};

// Classroom Missions Service
export const classroomMissionsService = {
  async createMission(sessionId, missionData) {
    const missionsRef = collection(db, 'sessions', sessionId, 'missions');
    const payload = {
      title: missionData.title || 'New Quest',
      description: missionData.description || '',
      emoji: missionData.emoji || '🛸',
      reward: missionData.reward || 15,
      status: missionData.status || 'available',
      completedBy: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    const missionDoc = await addDoc(missionsRef, payload);
    return missionDoc.id;
  },

  async updateMission(sessionId, missionId, updates) {
    const missionRef = doc(db, 'sessions', sessionId, 'missions', missionId);
    await updateDoc(missionRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  },

  async logMissionCompletion(sessionId, missionId, studentId) {
    const missionRef = doc(db, 'sessions', sessionId, 'missions', missionId);
    await updateDoc(missionRef, {
      completedBy: arrayUnion({ studentId, completedAt: Timestamp.now() }),
      status: 'completed',
      updatedAt: Timestamp.now()
    });
  },

  subscribeToMissions(sessionId, callback, errorCallback) {
    const missionsRef = collection(db, 'sessions', sessionId, 'missions');
    const q = query(missionsRef, orderBy('createdAt', 'asc'));
    return onSnapshot(q,
      (snapshot) => {
        const missions = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        callback(missions);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          console.error('Error subscribing to missions:', error);
        }
      }
    );
  }
};

// Classroom Idea Board Service
export const ideaBoardService = {
  async postIdea(sessionId, { authorId, authorName, text, category }) {
    const ideasRef = collection(db, 'sessions', sessionId, 'ideas');
    const payload = {
      authorId,
      authorName,
      text,
      category: category || 'idea',
      cheers: 0,
      createdAt: Timestamp.now()
    };
    const ideaDoc = await addDoc(ideasRef, payload);
    return ideaDoc.id;
  },

  async cheerIdea(sessionId, ideaId) {
    const ideaRef = doc(db, 'sessions', sessionId, 'ideas', ideaId);
    await updateDoc(ideaRef, {
      cheers: increment(1)
    });
  },

  subscribeToIdeas(sessionId, callback, errorCallback) {
    const ideasRef = collection(db, 'sessions', sessionId, 'ideas');
    const q = query(ideasRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q,
      (snapshot) => {
        const ideas = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        callback(ideas);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          console.error('Error subscribing to idea board:', error);
        }
      }
    );
  }
};

// Student Side Quest Service
export const sideQuestService = {
  async assignQuest(studentId, questData) {
    const questsRef = collection(db, 'users', studentId, 'sidequests');
    const payload = {
      title: questData.title || 'Mini Challenge',
      description: questData.description || 'Complete a fun task to earn stars.',
      reward: questData.reward || 10,
      status: questData.status || 'available',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    const questDoc = await addDoc(questsRef, payload);
    return questDoc.id;
  },

  async updateQuestStatus(studentId, questId, status) {
    const questRef = doc(db, 'users', studentId, 'sidequests', questId);
    await updateDoc(questRef, {
      status,
      updatedAt: Timestamp.now()
    });
  },

  async completeQuest(studentId, questId, reward = 15) {
    await this.updateQuestStatus(studentId, questId, 'completed');
    try {
      await gamificationService.awardPoints(studentId, null, reward, 'Side quest completed', 'sidequest');
    } catch (error) {
      console.warn('Could not award points for side quest:', error);
    }
  },

  subscribeToQuests(studentId, callback, errorCallback) {
    const questsRef = collection(db, 'users', studentId, 'sidequests');
    const q = query(questsRef, orderBy('createdAt', 'desc'));
    return onSnapshot(q,
      (snapshot) => {
        const quests = snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
        callback(quests);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          console.error('Error subscribing to side quests:', error);
        }
      }
    );
  }
};

// Courses Collection
export const coursesService = {
  async createCourse(courseData) {
    const processedData = {
      ...courseData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    const docRef = await addDoc(collection(db, 'courses'), processedData);
    return docRef.id;
  },

  async getCourse(courseId) {
    const docSnap = await getDoc(doc(db, 'courses', courseId));
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  },

  async getAllCourses() {
    const q = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  subscribeToCourses(callback, errorCallback) {
    // Try with orderBy first, fallback to simple query if index missing
    let q = query(collection(db, 'courses'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, 
      (snapshot) => {
        const courses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(courses);
      },
      (error) => {
        // If index missing, try without orderBy
        if (error.code === 'failed-precondition') {
          console.warn('Index missing for courses. Using simple query without sorting.');
          const simpleQuery = collection(db, 'courses');
          return onSnapshot(simpleQuery, 
            (snapshot) => {
              const courses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              // Sort manually
              courses.sort((a, b) => {
                const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
                const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
                return bTime - aTime;
              });
              callback(courses);
            },
            (fallbackError) => {
              if (errorCallback) {
                errorCallback(fallbackError);
              } else {
                console.error('Error in courses subscription (fallback):', fallbackError);
              }
            }
          );
        } else {
          if (errorCallback) {
            errorCallback(error);
          } else {
            console.error('Error in courses subscription:', error);
          }
        }
      }
    );
  },

  async updateCourse(courseId, courseData) {
    const processedData = {
      ...courseData,
      updatedAt: Timestamp.now()
    };
    await updateDoc(doc(db, 'courses', courseId), processedData);
  },

  async deleteCourse(courseId) {
    await deleteDoc(doc(db, 'courses', courseId));
  }
};

// Sessions Collection
export const sessionsService = {
  async createSession(sessionData) {
    const docRef = await addDoc(collection(db, 'sessions'), {
      ...sessionData,
      startDate: Timestamp.fromDate(new Date(sessionData.startDate)),
      endDate: Timestamp.fromDate(new Date(sessionData.endDate)),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  },

  async getSession(sessionId) {
    try {
      const docSnap = await getDoc(doc(db, 'sessions', sessionId));
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          startDate: timestampToDateString(data.startDate),
          endDate: timestampToDateString(data.endDate)
        };
      } else {
        // Session doesn't exist - return null without warning (caller will handle cleanup)
        return null;
      }
    } catch (error) {
      console.error(`❌ Error fetching session ${sessionId}:`, error);
      throw error;
    }
  },

  async getAllSessions() {
    const q = query(collection(db, 'sessions'), orderBy('startDate', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        startDate: timestampToDateString(data.startDate),
        endDate: timestampToDateString(data.endDate)
      };
    });
  },

  subscribeToSessions(callback, errorCallback) {
    const q = query(collection(db, 'sessions'), orderBy('startDate', 'desc'));
    return onSnapshot(q, 
      (snapshot) => {
        const sessions = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            startDate: timestampToDateString(data.startDate),
            endDate: timestampToDateString(data.endDate)
          };
        });
        callback(sessions);
      },
      (error) => {
        // If index missing, try without orderBy
        if (error.code === 'failed-precondition') {
          console.warn('Index missing for sessions. Using simple query without sorting.');
          const simpleQuery = collection(db, 'sessions');
          return onSnapshot(simpleQuery, 
            (snapshot) => {
              const sessions = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  startDate: timestampToDateString(data.startDate),
                  endDate: timestampToDateString(data.endDate)
                };
              });
              // Sort manually by startDate
              sessions.sort((a, b) => {
                const aDate = new Date(a.startDate).getTime();
                const bDate = new Date(b.startDate).getTime();
                return bDate - aDate;
              });
              callback(sessions);
            },
            (fallbackError) => {
              if (errorCallback) {
                errorCallback(fallbackError);
              } else {
                console.error('Error in sessions subscription (fallback):', fallbackError);
              }
            }
          );
        } else {
          if (errorCallback) {
            errorCallback(error);
          } else {
            console.error('Error in sessions subscription:', error);
          }
        }
      }
    );
  },

  async updateSession(sessionId, sessionData) {
    const updateData = {
      ...sessionData,
      updatedAt: Timestamp.now()
    };
    // Only update dates if provided
    if (sessionData.startDate) {
      updateData.startDate = Timestamp.fromDate(new Date(sessionData.startDate));
    }
    if (sessionData.endDate) {
      updateData.endDate = Timestamp.fromDate(new Date(sessionData.endDate));
    }
    await updateDoc(doc(db, 'sessions', sessionId), updateData);
  },

  async updateSessionClassroomUrl(sessionId, classroomUrl) {
    await updateDoc(doc(db, 'sessions', sessionId), {
      classroomUrl: classroomUrl || '',
      updatedAt: Timestamp.now()
    });
  },

  async deleteSession(sessionId) {
    await deleteDoc(doc(db, 'sessions', sessionId));
  }
};

// Homeworks Collection
export const homeworksService = {
  async createHomework(homeworkData) {
    const docRef = await addDoc(collection(db, 'homeworks'), {
      ...homeworkData,
      dueDate: Timestamp.fromDate(new Date(homeworkData.dueDate)),
      enabled: homeworkData.enabled !== undefined ? homeworkData.enabled : true, // Default to enabled
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  },

  async getHomework(homeworkId) {
    const docSnap = await getDoc(doc(db, 'homeworks', homeworkId));
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        dueDate: timestampToDateTimeString(data.dueDate)
      };
    }
    return null;
  },

  async getAllHomeworks() {
    const q = query(collection(db, 'homeworks'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        dueDate: timestampToDateTimeString(data.dueDate)
      };
    });
  },

  async getHomeworksBySession(sessionId) {
    try {
      console.log(`🔍 getHomeworksBySession called for sessionId: ${sessionId}`);
      const q = query(
        collection(db, 'homeworks'),
        where('sessionId', '==', sessionId),
        orderBy('dueDate', 'asc')
      );
      const querySnapshot = await getDocs(q);
      console.log(`📊 Query result for session ${sessionId}: ${querySnapshot.docs.length} homeworks found`);
      const homeworks = querySnapshot.docs.map(doc => {
        const data = doc.data();
        const homework = {
          id: doc.id,
          ...data,
          dueDate: timestampToDateTimeString(data.dueDate)
        };
        console.log(`📝 Homework found: "${homework.title}" (ID: ${homework.id}), enabled: ${homework.enabled}, sessionId: ${homework.sessionId}`);
        return homework;
      });
      return homeworks;
    } catch (error) {
      // If index missing, try without orderBy and sort manually
      if (error.code === 'failed-precondition') {
        console.warn('Index missing for homeworks query. Using simple query without sorting.');
        const simpleQuery = query(
          collection(db, 'homeworks'),
          where('sessionId', '==', sessionId)
        );
        const querySnapshot = await getDocs(simpleQuery);
        console.log(`📊 Fallback query result for session ${sessionId}: ${querySnapshot.docs.length} homeworks found`);
        const homeworks = querySnapshot.docs.map(doc => {
          const data = doc.data();
          const homework = {
            id: doc.id,
            ...data,
            dueDate: timestampToDateTimeString(data.dueDate)
          };
          console.log(`📝 Homework found (fallback): "${homework.title}" (ID: ${homework.id}), enabled: ${homework.enabled}, sessionId: ${homework.sessionId}`);
          return homework;
        });
        // Sort manually by dueDate (already converted to string by timestampToDateTimeString)
        homeworks.sort((a, b) => {
          const aTime = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          const bTime = b.dueDate ? new Date(b.dueDate).getTime() : 0;
          return aTime - bTime; // ascending order
        });
        return homeworks;
      }
      // Re-throw other errors
      throw error;
    }
  },

  subscribeToHomeworks(callback, errorCallback) {
    const q = query(collection(db, 'homeworks'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, 
      (snapshot) => {
        const homeworks = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dueDate: timestampToDateTimeString(data.dueDate)
          };
        });
        callback(homeworks);
      },
      (error) => {
        // If index missing, try without orderBy
        if (error.code === 'failed-precondition') {
          console.warn('Index missing for homeworks. Using simple query without sorting.');
          const simpleQuery = collection(db, 'homeworks');
          return onSnapshot(simpleQuery, 
            (snapshot) => {
              const homeworks = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  id: doc.id,
                  ...data,
                  dueDate: timestampToDateTimeString(data.dueDate)
                };
              });
              // Sort manually by createdAt
              homeworks.sort((a, b) => {
                const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
                const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
                return bTime - aTime;
              });
              callback(homeworks);
            },
            (fallbackError) => {
              if (errorCallback) {
                errorCallback(fallbackError);
              } else {
                console.error('Error in homeworks subscription (fallback):', fallbackError);
              }
            }
          );
        } else {
          if (errorCallback) {
            errorCallback(error);
          } else {
            console.error('Error in homeworks subscription:', error);
          }
        }
      }
    );
  },

  async updateHomework(homeworkId, homeworkData) {
    const updateData = {
      ...homeworkData,
      updatedAt: Timestamp.now()
    };
    // Only update dueDate if it's provided
    if (homeworkData.dueDate) {
      updateData.dueDate = Timestamp.fromDate(new Date(homeworkData.dueDate));
    }
    await updateDoc(doc(db, 'homeworks', homeworkId), updateData);
  },

  async deleteHomework(homeworkId) {
    await deleteDoc(doc(db, 'homeworks', homeworkId));
  }
};

// Student Courses Subcollection
export const studentCoursesService = {
  async assignCourseToStudent(studentId, courseId, adminId) {
    const studentCoursesRef = collection(db, 'users', studentId, 'studentCourses');
    await addDoc(studentCoursesRef, {
      courseId,
      assignedAt: Timestamp.now(),
      assignedBy: adminId
    });
  },

  async getStudentCourses(studentId) {
    const studentCoursesRef = collection(db, 'users', studentId, 'studentCourses');
    const querySnapshot = await getDocs(studentCoursesRef);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  async removeCourseFromStudent(studentId, courseAssignmentId) {
    await deleteDoc(doc(db, 'users', studentId, 'studentCourses', courseAssignmentId));
  },

  async updateStudentCourses(studentId, courseIds, adminId) {
    // Get existing assignments
    const existing = await this.getStudentCourses(studentId);
    
    // Remove all existing assignments
    for (const assignment of existing) {
      await this.removeCourseFromStudent(studentId, assignment.id);
    }
    
    // Add new assignments
    for (const courseId of courseIds) {
      await this.assignCourseToStudent(studentId, courseId, adminId);
    }
  }
};

// Student Sessions Subcollection
export const studentSessionsService = {
  async assignSessionToStudent(studentId, sessionId, adminId) {
    // First, verify the session exists
    const sessionDoc = await getDoc(doc(db, 'sessions', sessionId));
    if (!sessionDoc.exists()) {
      throw new Error(`Session ${sessionId} does not exist. Cannot assign a non-existent session.`);
    }

    const studentSessionsRef = collection(db, 'users', studentId, 'studentSessions');
    
    // Check if already assigned
    const existing = await getDocs(
      query(studentSessionsRef, where('sessionId', '==', sessionId))
    );
    
    if (!existing.empty) {
      // Already assigned, update status to active
      const existingDoc = existing.docs[0];
      await updateDoc(existingDoc.ref, {
        status: 'active',
        assignedAt: Timestamp.now(),
        assignedBy: adminId
      });
    } else {
      // Create new assignment
      await addDoc(studentSessionsRef, {
        sessionId,
        assignedAt: Timestamp.now(),
        assignedBy: adminId,
        status: 'active' // Changed from 'assigned' to 'active' so it shows up immediately
      });
    }

    // Also create a registration entry so it appears in the registration system
    try {
      const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
      // Check if registration already exists
      const existingReg = await getDocs(
        query(registrationsRef, where('studentId', '==', studentId))
      );
      
      if (existingReg.empty) {
        // Create registration entry with 'active' status
        await addDoc(registrationsRef, {
          studentId,
          sessionId,
          status: 'active',
          registeredAt: Timestamp.now(),
          approvedAt: Timestamp.now(),
          approvedBy: adminId
        });
      } else {
        // Update existing registration to active
        const regDoc = existingReg.docs[0];
        await updateDoc(regDoc.ref, {
          status: 'active',
          approvedAt: Timestamp.now(),
          approvedBy: adminId
        });
      }
    } catch (error) {
      console.warn('Could not create registration entry (may not have permission):', error);
      // Continue anyway - the studentSessions entry is enough
    }
  },

  async getStudentSessions(studentId) {
    const studentSessionsRef = collection(db, 'users', studentId, 'studentSessions');
    const querySnapshot = await getDocs(studentSessionsRef);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  subscribeToStudentSessions(studentId, callback, errorCallback) {
    const studentSessionsRef = collection(db, 'users', studentId, 'studentSessions');
    return onSnapshot(studentSessionsRef, 
      (snapshot) => {
        const sessions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(sessions);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          console.error('Error in student sessions subscription:', error);
        }
      }
    );
  },

  async removeStudentSession(assignmentId, studentId) {
    // If studentId is provided, use it; otherwise, we need to find it
    if (studentId) {
      await deleteDoc(doc(db, 'users', studentId, 'studentSessions', assignmentId));
    } else {
      // If studentId is not provided, we need to search for it
      // This is less efficient but handles the case where we only have the assignment ID
      throw new Error('studentId is required to remove student session');
    }
  },

  async removeStudentSessionByAssignmentId(assignmentId) {
    // Search for the assignment across all users (admin function)
    // Note: This is expensive and should be used sparingly
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(query(usersRef, where('type', '==', 'student')));
    
    for (const userDoc of usersSnapshot.docs) {
      const studentSessionsRef = collection(db, 'users', userDoc.id, 'studentSessions');
      const assignmentDoc = doc(studentSessionsRef, assignmentId);
      const assignmentSnap = await getDoc(assignmentDoc);
      
      if (assignmentSnap.exists()) {
        await deleteDoc(assignmentDoc);
        return { success: true, studentId: userDoc.id };
      }
    }
    
    return { success: false, message: 'Assignment not found' };
  }
};

// Homework Submissions Subcollection
export const homeworkSubmissionsService = {
  async createSubmission(homeworkId, submissionData) {
    const submissionsRef = collection(db, 'homeworks', homeworkId, 'submissions');
    const docRef = await addDoc(submissionsRef, {
      ...submissionData,
      submittedAt: Timestamp.now(),
      status: 'submitted'
    });
    return docRef.id;
  },

  async getSubmission(homeworkId, submissionId) {
    const docSnap = await getDoc(doc(db, 'homeworks', homeworkId, 'submissions', submissionId));
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        submittedAt: data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt
      };
    }
    return null;
  },

  async getStudentSubmission(homeworkId, studentId) {
    const submissionsRef = collection(db, 'homeworks', homeworkId, 'submissions');
    const q = query(submissionsRef, where('studentId', '==', studentId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt
      };
    }
    return null;
  },

  async getAllSubmissions(homeworkId) {
    const submissionsRef = collection(db, 'homeworks', homeworkId, 'submissions');
    const querySnapshot = await getDocs(submissionsRef);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        submittedAt: data.submittedAt?.toDate?.()?.toISOString() || data.submittedAt
      };
    });
  },

  async updateSubmission(homeworkId, submissionId, updateData) {
    const submissionRef = doc(db, 'homeworks', homeworkId, 'submissions', submissionId);
    const updateObj = {
      ...updateData,
      updatedAt: Timestamp.now()
    };
    if (updateData.gradedAt !== undefined) {
      updateObj.gradedAt = Timestamp.now();
    }
    await updateDoc(submissionRef, updateObj);
  },

  async gradeSubmission(homeworkId, submissionId, grade, feedback, gradedBy) {
    const submissionRef = doc(db, 'homeworks', homeworkId, 'submissions', submissionId);
    await updateDoc(submissionRef, {
      grade,
      feedback: feedback || '',
      gradedAt: Timestamp.now(),
      gradedBy,
      status: 'graded',
      updatedAt: Timestamp.now()
    });
  }
};

// Session Registrations Subcollection
export const sessionRegistrationsService = {
  async registerForSession(sessionId, studentId) {
    try {
      console.log('📝 Registering for session:', { sessionId, studentId });
      
      // Check if already registered
      const existingReg = await this.getStudentRegistration(sessionId, studentId);
      if (existingReg) {
        throw new Error('Already registered for this session');
      }

      // Check if session has maxStudents limit and if it's full
      const sessionRef = doc(db, 'sessions', sessionId);
      const sessionDoc = await getDoc(sessionRef);
      if (sessionDoc.exists()) {
        const sessionData = sessionDoc.data();
        
        // Check if session is past its end date (archived)
        if (sessionData.endDate) {
          const endDate = sessionData.endDate.toDate ? sessionData.endDate.toDate() : new Date(sessionData.endDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          
          if (endDate < today) {
            throw new Error('Registration is closed for this session as it has ended.');
          }
        }
        
        if (sessionData.maxStudents) {
          // Count active and pending registrations
          const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
          const registrationsSnapshot = await getDocs(registrationsRef);
          const activeAndPendingCount = registrationsSnapshot.docs.filter(doc => {
            const data = doc.data();
            return data.status === 'active' || data.status === 'pending';
          }).length;
          
          if (activeAndPendingCount >= sessionData.maxStudents) {
            throw new Error(`Session is full. Maximum ${sessionData.maxStudents} students allowed.`);
          }
        }
      }

      const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
      console.log('📝 Creating registration document...');
      const docRef = await addDoc(registrationsRef, {
        studentId,
        sessionId,
        status: 'pending',
        registeredAt: Timestamp.now()
      });
      console.log('✅ Registration document created:', docRef.id);

      // Also create entry in studentSessions subcollection (if user document exists)
      try {
        const studentSessionsRef = collection(db, 'users', studentId, 'studentSessions');
        await addDoc(studentSessionsRef, {
          sessionId,
          registrationId: docRef.id,
          status: 'pending',
          registeredAt: Timestamp.now()
        });
        console.log('✅ Student session entry created');
      } catch (studentSessionError) {
        // If user document doesn't exist yet, that's okay - the registration is still created
        console.warn('⚠️ Could not create studentSessions entry (user document may not exist yet):', studentSessionError);
      }

      return docRef.id;
    } catch (error) {
      console.error('❌ Error in registerForSession:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw error;
    }
  },

  async getStudentRegistration(sessionId, studentId) {
    const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
    const q = query(registrationsRef, where('studentId', '==', studentId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    }
    return null;
  },

  async approveRegistration(sessionId, registrationId, adminId) {
    const registrationRef = doc(db, 'sessions', sessionId, 'registrations', registrationId);
    const registrationSnap = await getDoc(registrationRef);
    
    if (!registrationSnap.exists()) {
      throw new Error('Registration not found');
    }

    const registration = registrationSnap.data();
    
    // Update registration
    await updateDoc(registrationRef, {
      status: 'active',
      approvedAt: Timestamp.now(),
      approvedBy: adminId,
      classroomEnabled: registration.classroomEnabled || false // Preserve existing classroomEnabled
    });

    // Update studentSessions subcollection
    const studentSessionsRef = collection(db, 'users', registration.studentId, 'studentSessions');
    const studentSessionsQuery = query(studentSessionsRef, where('sessionId', '==', sessionId));
    const studentSessionsSnap = await getDocs(studentSessionsQuery);
    if (!studentSessionsSnap.empty) {
      const studentSessionDoc = studentSessionsSnap.docs[0];
      await updateDoc(studentSessionDoc.ref, {
        status: 'active',
        approvedAt: Timestamp.now()
      });
    }
  },

  // DEPRECATED: Old session-level functions (keeping for backward compatibility)
  async enableClassroomForStudent(sessionId, registrationId, enabled) {
    const registrationRef = doc(db, 'sessions', sessionId, 'registrations', registrationId);
    await updateDoc(registrationRef, {
      classroomEnabled: enabled,
      classroomUpdatedAt: Timestamp.now()
    });
  },

  async enableClassroomForAllStudents(sessionId, enabled) {
    const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
    const q = query(registrationsRef, where('status', '==', 'active'));
    const querySnapshot = await getDocs(q);
    
    const updatePromises = querySnapshot.docs.map(doc => 
      updateDoc(doc.ref, {
        classroomEnabled: enabled,
        classroomUpdatedAt: Timestamp.now()
      })
    );
    
    await Promise.all(updatePromises);
  },

  // NEW: Topic-level classroom access functions
  async enableTopicClassroom(sessionId, topicName, enabled) {
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      throw new Error('Session not found');
    }

    const topicClassrooms = sessionDoc.data().topicClassrooms || {};
    topicClassrooms[topicName] = {
      enabled: enabled,
      enabledAt: Timestamp.now()
    };

    await updateDoc(sessionRef, {
      topicClassrooms: topicClassrooms
    });
  },

  async getTopicClassroomStatus(sessionId, topicName) {
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      return { enabled: false };
    }

    const topicClassrooms = sessionDoc.data().topicClassrooms || {};
    return topicClassrooms[topicName] || { enabled: false };
  },

  async getAllTopicClassroomStatus(sessionId) {
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);
    
    if (!sessionDoc.exists()) {
      return {};
    }

    return sessionDoc.data().topicClassrooms || {};
  },

  async cancelRegistration(sessionId, registrationId, cancelledBy, isAdmin) {
    const registrationRef = doc(db, 'sessions', sessionId, 'registrations', registrationId);
    const registrationSnap = await getDoc(registrationRef);
    
    if (!registrationSnap.exists()) {
      throw new Error('Registration not found');
    }

    const registration = registrationSnap.data();
    
    // Update registration
    await updateDoc(registrationRef, {
      status: 'cancelled',
      cancelledAt: Timestamp.now(),
      cancelledBy
    });

    // Update studentSessions subcollection
    const studentSessionsRef = collection(db, 'users', registration.studentId, 'studentSessions');
    const studentSessionsQuery = query(studentSessionsRef, where('sessionId', '==', sessionId));
    const studentSessionsSnap = await getDocs(studentSessionsQuery);
    if (!studentSessionsSnap.empty) {
      const studentSessionDoc = studentSessionsSnap.docs[0];
      await updateDoc(studentSessionDoc.ref, {
        status: 'cancelled',
        cancelledAt: Timestamp.now()
      });
    }
  },

  async getSessionRegistrations(sessionId) {
    try {
      const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
      const q = query(registrationsRef, orderBy('registeredAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      // If index missing, try without orderBy
      if (error.code === 'failed-precondition') {
        console.warn('Index missing for registrations. Using simple query without sorting.');
        const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
        const querySnapshot = await getDocs(registrationsRef);
        const registrations = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort manually by registeredAt
        registrations.sort((a, b) => {
          const aTime = a.registeredAt?.toMillis?.() || a.registeredAt || 0;
          const bTime = b.registeredAt?.toMillis?.() || b.registeredAt || 0;
          return bTime - aTime;
        });
        return registrations;
      }
      throw error;
    }
  },

  subscribeToSessionRegistrations(sessionId, callback, errorCallback) {
    const registrationsRef = collection(db, 'sessions', sessionId, 'registrations');
    const q = query(registrationsRef, orderBy('registeredAt', 'desc'));
    
    let unsubscribeFn = null;
    
    unsubscribeFn = onSnapshot(q,
      (snapshot) => {
        const registrations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        callback(registrations);
      },
      (error) => {
        // If index missing, try without orderBy
        if (error.code === 'failed-precondition') {
          console.warn('Index missing for registrations. Using simple query without sorting.');
          const simpleQuery = collection(db, 'sessions', sessionId, 'registrations');
          unsubscribeFn = onSnapshot(simpleQuery,
            (snapshot) => {
              const registrations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              // Sort manually by registeredAt
              registrations.sort((a, b) => {
                const aTime = a.registeredAt?.toMillis?.() || a.registeredAt || 0;
                const bTime = b.registeredAt?.toMillis?.() || b.registeredAt || 0;
                return bTime - aTime;
              });
              callback(registrations);
            },
            (fallbackError) => {
              if (errorCallback) {
                errorCallback(fallbackError);
              } else {
                console.error('Error in session registrations subscription (fallback):', fallbackError);
              }
            }
          );
        } else {
          if (errorCallback) {
            errorCallback(error);
          } else {
            console.error('Error in session registrations subscription:', error);
          }
        }
      }
    );
    
    return () => {
      if (unsubscribeFn) {
        unsubscribeFn();
      }
    };
  },

  async getStudentRegistrations(studentId) {
    // Get all sessions and check for registrations
    const sessionsRef = collection(db, 'sessions');
    const sessionsSnap = await getDocs(sessionsRef);
    const registrations = [];

    for (const sessionDoc of sessionsSnap.docs) {
      const registrationsRef = collection(db, 'sessions', sessionDoc.id, 'registrations');
      const q = query(registrationsRef, where('studentId', '==', studentId));
      const regSnap = await getDocs(q);
      if (!regSnap.empty) {
        const reg = regSnap.docs[0];
        registrations.push({
          id: reg.id,
          sessionId: sessionDoc.id,
          ...reg.data()
        });
      }
    }

    return registrations;
  },

  subscribeToStudentRegistrations(studentId, callback, errorCallback) {
    // Subscribe to all sessions and filter registrations
    const sessionsRef = collection(db, 'sessions');
    return onSnapshot(sessionsRef,
      async (snapshot) => {
        const registrations = [];
        for (const sessionDoc of snapshot.docs) {
          const registrationsRef = collection(db, 'sessions', sessionDoc.id, 'registrations');
          const q = query(registrationsRef, where('studentId', '==', studentId));
          const regSnap = await getDocs(q);
          if (!regSnap.empty) {
            const reg = regSnap.docs[0];
            registrations.push({
              id: reg.id,
              sessionId: sessionDoc.id,
              ...reg.data()
            });
          }
        }
        callback(registrations);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          console.error('Error in student registrations subscription:', error);
        }
      }
    );
  }
};

// Attendance Subcollection
export const attendanceService = {
  async markAttendance(sessionId, attendanceData) {
    const attendanceRef = collection(db, 'sessions', sessionId, 'attendance');
    const docRef = await addDoc(attendanceRef, {
      ...attendanceData,
      topicDate: Timestamp.fromDate(new Date(attendanceData.topicDate)),
      markedAt: Timestamp.now()
    });
    return docRef.id;
  },

  async updateAttendance(sessionId, attendanceId, status, markedBy) {
    const attendanceRef = doc(db, 'sessions', sessionId, 'attendance', attendanceId);
    await updateDoc(attendanceRef, {
      status,
      markedAt: Timestamp.now(),
      markedBy
    });
  },

  async getStudentAttendance(sessionId, studentId) {
    const attendanceRef = collection(db, 'sessions', sessionId, 'attendance');
    const q = query(attendanceRef, where('studentId', '==', studentId));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        topicDate: timestampToDateString(data.topicDate)
      };
    });
  },

  async getSessionAttendance(sessionId) {
    const attendanceRef = collection(db, 'sessions', sessionId, 'attendance');
    const q = query(attendanceRef, orderBy('topicDate', 'asc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        topicDate: timestampToDateString(data.topicDate)
      };
    });
  },

  subscribeToSessionAttendance(sessionId, callback, errorCallback) {
    const attendanceRef = collection(db, 'sessions', sessionId, 'attendance');
    const q = query(attendanceRef, orderBy('topicDate', 'asc'));
    return onSnapshot(q,
      (snapshot) => {
        const attendance = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            topicDate: timestampToDateString(data.topicDate)
          };
        });
        callback(attendance);
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        } else {
          console.error('Error in attendance subscription:', error);
        }
      }
    );
  }
};

// ============================================
// GAMIFICATION & REWARDS SYSTEM
// ============================================

export const gamificationService = {
  // Award points to a student
  async awardPoints(studentId, sessionId, points, reason, activityType) {
    const pointsRef = collection(db, 'users', studentId, 'points');
    await addDoc(pointsRef, {
      sessionId,
      points,
      reason,
      activityType, // 'attendance', 'participation', 'exercise', 'help', 'streak'
      awardedAt: Timestamp.now()
    });

    // Update total points in user document
    const userRef = doc(db, 'users', studentId);
    const userSnap = await getDoc(userRef);
    const currentTotal = userSnap.data()?.totalPoints || 0;
    await updateDoc(userRef, {
      totalPoints: currentTotal + points,
      updatedAt: Timestamp.now()
    });
  },

  // Get student's total points
  async getStudentPoints(studentId) {
    const userSnap = await getDoc(doc(db, 'users', studentId));
    if (userSnap.exists()) {
      return userSnap.data().totalPoints || 0;
    }
    return 0;
  },

  // Get student's points history
  async getPointsHistory(studentId, limitCount = 50) {
    const pointsRef = collection(db, 'users', studentId, 'points');
    const q = query(pointsRef, orderBy('awardedAt', 'desc'), limit(limitCount));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      awardedAt: doc.data().awardedAt?.toDate?.() || doc.data().awardedAt
    }));
  },

  // Award a badge to a student
  async awardBadge(studentId, badgeId, badgeName, badgeIcon, description) {
    const badgesRef = collection(db, 'users', studentId, 'badges');
    
    // Check if badge already exists
    const existingBadges = await getDocs(
      query(badgesRef, where('badgeId', '==', badgeId))
    );
    
    if (existingBadges.empty) {
      await addDoc(badgesRef, {
        badgeId,
        badgeName,
        badgeIcon,
        description,
        awardedAt: Timestamp.now()
      });
      return true; // New badge awarded
    }
    return false; // Badge already exists
  },

  // Get student's badges
  async getStudentBadges(studentId) {
    const badgesRef = collection(db, 'users', studentId, 'badges');
    const q = query(badgesRef, orderBy('awardedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      awardedAt: doc.data().awardedAt?.toDate?.() || doc.data().awardedAt
    }));
  },

  // Get leaderboard
  async getLeaderboard(period = 'all', limitCount = 50) {
    // period: 'all', 'week', 'month'
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('type', '==', 'student'), orderBy('totalPoints', 'desc'), limit(limitCount));
    
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        totalPoints: doc.data().totalPoints || 0
      }));
    } catch (error) {
      // If index missing, get all and sort manually
      if (error.code === 'failed-precondition') {
        const allUsers = await getDocs(query(usersRef, where('type', '==', 'student')));
        const students = allUsers.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name,
          totalPoints: doc.data().totalPoints || 0
        }));
        students.sort((a, b) => (b.totalPoints || 0) - (a.totalPoints || 0));
        return students.slice(0, limitCount);
      }
      throw error;
    }
  },

  // Subscribe to student points
  subscribeToStudentPoints(studentId, callback) {
    const userRef = doc(db, 'users', studentId);
    return onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data().totalPoints || 0);
      } else {
        callback(0);
      }
    });
  },

  // Subscribe to student badges
  subscribeToStudentBadges(studentId, callback) {
    const badgesRef = collection(db, 'users', studentId, 'badges');
    const q = query(badgesRef, orderBy('awardedAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const badges = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        awardedAt: doc.data().awardedAt?.toDate?.() || doc.data().awardedAt
      }));
      callback(badges);
    });
  },

  // Check and award badges based on achievements
  async checkAndAwardBadges(studentId, sessionId) {
    const pointsHistory = await this.getPointsHistory(studentId, 1000);
    const badges = await this.getStudentBadges(studentId);
    const badgeIds = badges.map(b => b.badgeId);
    const newBadges = [];

    // Early Bird Badge - Joined 5 classes early
    const earlyJoins = pointsHistory.filter(p => p.activityType === 'attendance' && p.reason?.includes('early')).length;
    if (earlyJoins >= 5 && !badgeIds.includes('early-bird')) {
      await this.awardBadge(studentId, 'early-bird', 'Early Bird', '🏆', 'Joined 5 classes early');
      newBadges.push('early-bird');
    }

    // Question Master - Asked 10 questions
    const questions = pointsHistory.filter(p => p.activityType === 'participation' && p.reason?.includes('question')).length;
    if (questions >= 10 && !badgeIds.includes('question-master')) {
      await this.awardBadge(studentId, 'question-master', 'Question Master', '🎯', 'Asked 10 questions');
      newBadges.push('question-master');
    }

    // Helper Badge - Helped 5 classmates
    const helps = pointsHistory.filter(p => p.activityType === 'help').length;
    if (helps >= 5 && !badgeIds.includes('helper')) {
      await this.awardBadge(studentId, 'helper', 'Helper', '💡', 'Helped 5 classmates');
      newBadges.push('helper');
    }

    // Perfect Week - 100% attendance for a week
    // This would need additional logic to check weekly attendance
    // For now, we'll implement a simpler version

    return newBadges;
  }
};

// ============================================
// EMOJI REACTIONS SYSTEM
// ============================================

export const reactionsService = {
  // Add a reaction to a session
  async addReaction(sessionId, studentId, emoji) {
    const reactionsRef = collection(db, 'sessions', sessionId, 'reactions');
    
    // Check if student already reacted with this emoji
    const existingReactions = await getDocs(
      query(reactionsRef, where('studentId', '==', studentId), where('emoji', '==', emoji))
    );

    if (!existingReactions.empty) {
      // Remove existing reaction
      const reactionDoc = existingReactions.docs[0];
      await deleteDoc(reactionDoc.ref);
      return false; // Reaction removed
    } else {
      // Add new reaction
      await addDoc(reactionsRef, {
        studentId,
        emoji,
        reactedAt: Timestamp.now()
      });
      return true; // Reaction added
    }
  },

  // Get all reactions for a session
  async getSessionReactions(sessionId) {
    const reactionsRef = collection(db, 'sessions', sessionId, 'reactions');
    const querySnapshot = await getDocs(reactionsRef);
    
    // Group by emoji
    const reactionsMap = {};
    querySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const emoji = data.emoji;
      if (!reactionsMap[emoji]) {
        reactionsMap[emoji] = {
          emoji,
          count: 0,
          students: []
        };
      }
      reactionsMap[emoji].count++;
      reactionsMap[emoji].students.push(data.studentId);
    });

    return Object.values(reactionsMap);
  },

  // Subscribe to reactions
  subscribeToReactions(sessionId, callback) {
    const reactionsRef = collection(db, 'sessions', sessionId, 'reactions');
    return onSnapshot(reactionsRef, (snapshot) => {
      const reactionsMap = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const emoji = data.emoji;
        if (!reactionsMap[emoji]) {
          reactionsMap[emoji] = {
            emoji,
            count: 0,
            students: []
          };
        }
        reactionsMap[emoji].count++;
        reactionsMap[emoji].students.push(data.studentId);
      });
      callback(Object.values(reactionsMap));
    });
  },

  // Get student's reaction for a session
  async getStudentReaction(sessionId, studentId) {
    const reactionsRef = collection(db, 'sessions', sessionId, 'reactions');
    const q = query(reactionsRef, where('studentId', '==', studentId));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data().emoji;
    }
    return null;
  }
};

// ============================================
// LIVE POLLS & QUIZZES SYSTEM
// ============================================

export const pollsService = {
  // Create a poll
  async createPoll(sessionId, pollData) {
    const pollsRef = collection(db, 'sessions', sessionId, 'polls');
    const docRef = await addDoc(pollsRef, {
      ...pollData,
      createdAt: Timestamp.now(),
      status: 'active' // 'active', 'closed'
    });
    return docRef.id;
  },

  // Submit a poll response
  async submitPollResponse(sessionId, pollId, studentId, optionIndex) {
    const responsesRef = collection(db, 'sessions', sessionId, 'polls', pollId, 'responses');
    
    // Check if student already responded
    const existingResponse = await getDocs(
      query(responsesRef, where('studentId', '==', studentId))
    );

    if (!existingResponse.empty) {
      // Update existing response
      const responseDoc = existingResponse.docs[0];
      await updateDoc(responseDoc.ref, {
        optionIndex,
        respondedAt: Timestamp.now()
      });
    } else {
      // Create new response
      await addDoc(responsesRef, {
        studentId,
        optionIndex,
        respondedAt: Timestamp.now()
      });
    }
  },

  // Get poll results
  async getPollResults(sessionId, pollId) {
    const pollRef = doc(db, 'sessions', sessionId, 'polls', pollId);
    const pollSnap = await getDoc(pollRef);
    
    if (!pollSnap.exists()) return null;

    const pollData = pollSnap.data();
    const responsesRef = collection(db, 'sessions', sessionId, 'polls', pollId, 'responses');
    const responsesSnap = await getDocs(responsesRef);

    // Count responses by option
    const results = {};
    pollData.options.forEach((_, index) => {
      results[index] = { count: 0, percentage: 0 };
    });

    responsesSnap.docs.forEach(doc => {
      const optionIndex = doc.data().optionIndex;
      if (results[optionIndex]) {
        results[optionIndex].count++;
      }
    });

    const totalResponses = responsesSnap.size;
    Object.keys(results).forEach(index => {
      results[index].percentage = totalResponses > 0 
        ? Math.round((results[index].count / totalResponses) * 100) 
        : 0;
    });

    return {
      poll: pollData,
      results,
      totalResponses
    };
  },

  // Close a poll
  async closePoll(sessionId, pollId) {
    const pollRef = doc(db, 'sessions', sessionId, 'polls', pollId);
    await updateDoc(pollRef, {
      status: 'closed',
      closedAt: Timestamp.now()
    });
  },

  // Get active polls for a session
  async getActivePolls(sessionId) {
    const pollsRef = collection(db, 'sessions', sessionId, 'polls');
    const q = query(pollsRef, where('status', '==', 'active'), orderBy('createdAt', 'desc'));
    
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
    } catch (error) {
      // Fallback without orderBy
      if (error.code === 'failed-precondition') {
        const q2 = query(pollsRef, where('status', '==', 'active'));
        const querySnapshot = await getDocs(q2);
        const polls = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
        }));
        polls.sort((a, b) => {
          const aTime = a.createdAt?.getTime?.() || 0;
          const bTime = b.createdAt?.getTime?.() || 0;
          return bTime - aTime;
        });
        return polls;
      }
      throw error;
    }
  },

  // Subscribe to polls
  subscribeToPolls(sessionId, callback) {
    const pollsRef = collection(db, 'sessions', sessionId, 'polls');
    const q = query(pollsRef, where('status', '==', 'active'), orderBy('createdAt', 'desc'));
    
    return onSnapshot(q,
      (snapshot) => {
        const polls = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
        }));
        callback(polls);
      },
      (error) => {
        // Fallback without orderBy
        if (error.code === 'failed-precondition') {
          const simpleQuery = query(pollsRef, where('status', '==', 'active'));
          return onSnapshot(simpleQuery, (snapshot) => {
            const polls = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
            }));
            polls.sort((a, b) => {
              const aTime = a.createdAt?.getTime?.() || 0;
              const bTime = b.createdAt?.getTime?.() || 0;
              return bTime - aTime;
            });
            callback(polls);
          });
        }
        console.error('Error subscribing to polls:', error);
        callback([]);
      }
    );
  },

  // Subscribe to poll results
  subscribeToPollResults(sessionId, pollId, callback) {
    const responsesRef = collection(db, 'sessions', sessionId, 'polls', pollId, 'responses');
    return onSnapshot(responsesRef, async () => {
      const results = await this.getPollResults(sessionId, pollId);
      callback(results);
    });
  }
};

// ============================================
// PROGRESS TRACKING SYSTEM
// ============================================

export const progressService = {
  // Track session attendance
  async trackSessionAttendance(studentId, sessionId, joinedEarly = false) {
    const attendanceRef = collection(db, 'users', studentId, 'sessionAttendance');
    
    // Check if already tracked today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStart = Timestamp.fromDate(today);
    const todayEnd = Timestamp.fromDate(new Date(today.getTime() + 24 * 60 * 60 * 1000));
    
    const existing = await getDocs(
      query(
        attendanceRef,
        where('sessionId', '==', sessionId),
        where('attendedAt', '>=', todayStart),
        where('attendedAt', '<', todayEnd)
      )
    );

    if (existing.empty) {
      await addDoc(attendanceRef, {
        sessionId,
        attendedAt: Timestamp.now(),
        joinedEarly
      });

      // Award points
      if (joinedEarly) {
        await gamificationService.awardPoints(studentId, sessionId, 10, 'Joined class early', 'attendance');
      } else {
        await gamificationService.awardPoints(studentId, sessionId, 5, 'Attended class', 'attendance');
      }

      // Check for streaks and badges
      await this.updateAttendanceStreak(studentId);
      await gamificationService.checkAndAwardBadges(studentId, sessionId);
    }
  },

  // Update attendance streak
  async updateAttendanceStreak(studentId) {
    const attendanceRef = collection(db, 'users', studentId, 'sessionAttendance');
    const q = query(attendanceRef, orderBy('attendedAt', 'desc'), limit(30));
    const attendanceSnap = await getDocs(q);
    
    const attendances = attendanceSnap.docs.map(doc => ({
      ...doc.data(),
      attendedAt: doc.data().attendedAt?.toDate?.() || doc.data().attendedAt
    }));

    // Calculate current streak
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    for (let i = 0; i < attendances.length; i++) {
      const attDate = new Date(attendances[i].attendedAt);
      attDate.setHours(0, 0, 0, 0);
      const daysDiff = Math.floor((today - attDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff === i) {
        streak++;
      } else {
        break;
      }
    }

    // Update user document
    const userRef = doc(db, 'users', studentId);
    await updateDoc(userRef, {
      attendanceStreak: streak,
      updatedAt: Timestamp.now()
    });

    // Award streak bonus points
    if (streak > 0 && streak % 7 === 0) {
      await gamificationService.awardPoints(studentId, null, 50, `${streak} day attendance streak!`, 'streak');
    }
  },

  // Get student progress
  async getStudentProgress(studentId, sessionId) {
    const userSnap = await getDoc(doc(db, 'users', studentId));
    const userData = userSnap.data();
    
    // Get attendance for this session
    const attendanceRef = collection(db, 'users', studentId, 'sessionAttendance');
    const sessionAttendance = await getDocs(
      query(attendanceRef, where('sessionId', '==', sessionId))
    );

    return {
      totalPoints: userData?.totalPoints || 0,
      attendanceStreak: userData?.attendanceStreak || 0,
      sessionAttendanceCount: sessionAttendance.size,
      badges: await gamificationService.getStudentBadges(studentId)
    };
  },

  // Subscribe to student progress
  subscribeToStudentProgress(studentId, callback) {
    const userRef = doc(db, 'users', studentId);
    return onSnapshot(userRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        callback({
          totalPoints: data.totalPoints || 0,
          attendanceStreak: data.attendanceStreak || 0
        });
      } else {
        callback({ totalPoints: 0, attendanceStreak: 0 });
      }
    });
  },

  // Learning Cards Progress
  async markLearningCardComplete(userId, courseId, topicName, cardId) {
    const progressRef = doc(db, 'users', userId, 'learningCardProgress', `${courseId}_${topicName}`);
    const progressDoc = await getDoc(progressRef);
    
    const completedCards = progressDoc.exists() 
      ? progressDoc.data().completedCards || []
      : [];
    
    if (!completedCards.includes(cardId)) {
      completedCards.push(cardId);
      await setDoc(progressRef, {
        courseId,
        topicName,
        completedCards,
        updatedAt: Timestamp.now()
      }, { merge: true });
    }
  },

  async getLearningCardProgress(userId, courseId, topicName) {
    try {
      const progressRef = doc(db, 'users', userId, 'learningCardProgress', `${courseId}_${topicName}`);
      const progressDoc = await getDoc(progressRef);
      if (progressDoc.exists()) {
        return progressDoc.data();
      }
      return null;
    } catch (error) {
      console.debug('No learning card progress found');
      return null;
    }
  },

  async getAllLearningCardProgress(userId) {
    try {
      const progressRef = collection(db, 'users', userId, 'learningCardProgress');
      const snapshot = await getDocs(progressRef);
      const progress = {};
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        progress[`${data.courseId}_${data.topicName}`] = data;
      });
      return progress;
    } catch (error) {
      console.debug('Error loading learning card progress');
      return {};
    }
  }
};

// ============================================
// CLASS CHAT & Q&A SYSTEM
// ============================================

export const chatService = {
  // Send a message
  async sendMessage(sessionId, studentId, message, messageType = 'chat') {
    // messageType: 'chat', 'question'
    const messagesRef = collection(db, 'sessions', sessionId, 'messages');
    await addDoc(messagesRef, {
      studentId,
      message,
      messageType,
      createdAt: Timestamp.now(),
      status: messageType === 'question' ? 'pending' : 'sent' // 'pending', 'answered', 'sent'
    });
  },

  // Answer a question
  async answerQuestion(sessionId, messageId, answer, answeredBy) {
    const messageRef = doc(db, 'sessions', sessionId, 'messages', messageId);
    await updateDoc(messageRef, {
      answer,
      answeredBy,
      answeredAt: Timestamp.now(),
      status: 'answered'
    });
  },

  // Get messages for a session
  async getMessages(sessionId, limitCount = 100) {
    const messagesRef = collection(db, 'sessions', sessionId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(limitCount));
    
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
    } catch (error) {
      if (error.code === 'failed-precondition') {
        const simpleQuery = query(messagesRef, limit(limitCount));
        const querySnapshot = await getDocs(simpleQuery);
        const messages = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
        }));
        messages.sort((a, b) => {
          const aTime = a.createdAt?.getTime?.() || 0;
          const bTime = b.createdAt?.getTime?.() || 0;
          return bTime - aTime;
        });
        return messages;
      }
      throw error;
    }
  },

  // Subscribe to messages
  subscribeToMessages(sessionId, callback) {
    const messagesRef = collection(db, 'sessions', sessionId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'desc'), limit(100));
    
    return onSnapshot(q,
      (snapshot) => {
        const messages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
        }));
        callback(messages);
      },
      (error) => {
        if (error.code === 'failed-precondition') {
          const simpleQuery = query(messagesRef, limit(100));
          return onSnapshot(simpleQuery, (snapshot) => {
            const messages = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
            }));
            messages.sort((a, b) => {
              const aTime = a.createdAt?.getTime?.() || 0;
              const bTime = b.createdAt?.getTime?.() || 0;
              return bTime - aTime;
            });
            callback(messages);
          });
        }
        console.error('Error subscribing to messages:', error);
        callback([]);
      }
    );
  },

  // Get pending questions
  async getPendingQuestions(sessionId) {
    const messagesRef = collection(db, 'sessions', sessionId, 'messages');
    const q = query(messagesRef, where('messageType', '==', 'question'), where('status', '==', 'pending'), orderBy('createdAt', 'asc'));
    
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
    } catch (error) {
      if (error.code === 'failed-precondition') {
        const simpleQuery = query(messagesRef, where('messageType', '==', 'question'), where('status', '==', 'pending'));
        const querySnapshot = await getDocs(simpleQuery);
        const questions = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
        }));
        questions.sort((a, b) => {
          const aTime = a.createdAt?.getTime?.() || 0;
          const bTime = b.createdAt?.getTime?.() || 0;
          return aTime - bTime;
        });
        return questions;
      }
      throw error;
    }
  }
};

// ============================================
// STUDY GROUPS SYSTEM
// ============================================

export const studyGroupsService = {
  // Create a study group
  async createStudyGroup(sessionId, groupData) {
    const groupsRef = collection(db, 'sessions', sessionId, 'studyGroups');
    const docRef = await addDoc(groupsRef, {
      ...groupData,
      createdAt: Timestamp.now(),
      memberCount: 1
    });
    return docRef.id;
  },

  // Join a study group
  async joinStudyGroup(sessionId, groupId, studentId) {
    const membersRef = collection(db, 'sessions', sessionId, 'studyGroups', groupId, 'members');
    
    // Check if already a member
    const existing = await getDocs(query(membersRef, where('studentId', '==', studentId)));
    if (!existing.empty) {
      return false; // Already a member
    }

    await addDoc(membersRef, {
      studentId,
      joinedAt: Timestamp.now()
    });

    // Update member count
    const groupRef = doc(db, 'sessions', sessionId, 'studyGroups', groupId);
    const groupSnap = await getDoc(groupRef);
    const currentCount = groupSnap.data()?.memberCount || 0;
    await updateDoc(groupRef, {
      memberCount: currentCount + 1
    });

    return true;
  },

  // Get study groups for a session
  async getStudyGroups(sessionId) {
    const groupsRef = collection(db, 'sessions', sessionId, 'studyGroups');
    const querySnapshot = await getDocs(groupsRef);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
    }));
  },

  // Get group members
  async getGroupMembers(sessionId, groupId) {
    const membersRef = collection(db, 'sessions', sessionId, 'studyGroups', groupId, 'members');
    const querySnapshot = await getDocs(membersRef);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      joinedAt: doc.data().joinedAt?.toDate?.() || doc.data().joinedAt
    }));
  }
};

